// see comment in modules, but regardless of the choice there this can
// serve as a selector file for char recruitment page and character details
import _ from 'lodash';
import { createSelector } from 'reselect';
import { RecruitmentActivityType } from '../../../../../shared/types/recruitmentTypes';
import { TRecruitmentProfile } from '../libs/store';
import { allRaidsSelector } from '../../../../../shared/frontend/scripts/selectors/global';
import { getScheduleHighlights } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { serializeRecruitmentProfile } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { DIFFICULTY_SLUGS_DESCENDING } from '../../../shared/enums';
import { getRequirementHighlights } from '../shared/recruitment/utils';

/*
 * character details:
 */
const characterDetailsState = (st: any) => st?.characterDetails;
export const characterSelector = createSelector(characterDetailsState, ({ character }) => character);
export const characterCustomizationsSelector = createSelector(
  characterDetailsState,
  ({ characterCustomizations }) => characterCustomizations
);
export const characterIdSelector = createSelector(characterDetailsState, ({ character }) => character?.id);
export const characterPersonaIdSelector = createSelector(
  characterDetailsState,
  ({ character }) => character?.persona_id
);
export const characterUserSelector = createSelector(characterDetailsState, ({ user }) => user);
export const characterTeamSelector = createSelector(characterDetailsState, ({ team }) => team);

/*
 * character recruitment page
 */

const characterRecruitmentPageStateSelector = (st: any) => st.characterRecruitment;
export const characterTargetIdSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ currentEntityId }) => currentEntityId
);

const _applicationStateSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ application }) => application
);

export const applicationModalVisibilitySelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ applicationModalOpen }) => applicationModalOpen
);
export const applicationErrorMsgSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ errorMessage }) => errorMessage
);
export const threadIdSelector = createSelector(_applicationStateSelector, ({ threadId }) => threadId);
export const applicationStatusSelector = createSelector(_applicationStateSelector, ({ loading, loaded, error }) => ({
  loading,
  loaded,
  error
}));

const profilesMainCharacterDetailsSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ recruitmentProfileDetails }) => recruitmentProfileDetails
);

export const characterRecruitmentProfilePageStatusSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ loading, loaded, error }) => ({
    loading,
    loaded,
    error
  })
);

// the profile for which the user wants to apply with
export const sourceProfileIdSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ sourceProfileId }) => sourceProfileId
);
export const targetProfileIdSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ targetProfileId }) => targetProfileId
);
export const applicationMessageSelector = createSelector(
  characterRecruitmentPageStateSelector,
  ({ message }) => message
);

export const applicationIdsSelector = createSelector(
  sourceProfileIdSelector,
  targetProfileIdSelector,
  applicationMessageSelector,
  (sourceProfileId, targetProfileId, message) =>
    message ? { targetProfileId, sourceProfileId, message } : { targetProfileId, sourceProfileId }
);

const buildRaidProgressHighlights = ({ raidProgress }: any, allRaids: any) => {
  const raidHighlights = [];

  for (const raid of allRaids) {
    const matchedRaidProgress = raidProgress.filter((r: any) => r.raid === raid.slug);
    if (matchedRaidProgress.length === 0) {
      continue;
    }

    const [progress] = matchedRaidProgress;

    const thisRaidHighlights = [];
    for (const difficulty of DIFFICULTY_SLUGS_DESCENDING) {
      const numDefeated = progress.encountersDefeated[difficulty].length;
      const showStat = numDefeated > 0;

      if (showStat) {
        thisRaidHighlights.push({
          raidName: raid.name,
          numDefeated,
          total: raid.encounters.length,
          difficulty,

          value: `${numDefeated}/${raid.encounters.length}`,
          label: raid.short_name
        });

        if (numDefeated === raid.encounters.length) {
          // we've cleared the raid on a higher difficulty so don't care about lower ones
          break;
        }

        if (thisRaidHighlights.length === 2) {
          // we don't care about more than their top 2 progression difficulties
          break;
        }
      }
    }

    raidHighlights.push(...thisRaidHighlights);
  }

  return raidHighlights;
};

const getProgressHighlights = (profile: TRecruitmentProfile, details: any, allRaids: any) => {
  const { activity_type } = profile;

  switch (activity_type) {
    case RecruitmentActivityType.GuildRaids: {
      return [...buildRaidProgressHighlights(details, allRaids)];
    }
    default: {
      if (details.character) {
        return [
          {
            label: 'Achievement Points',
            value: details.character.achievementPoints
          }
        ];
      }

      return [];
    }
  }
};

// gets a list of the profiles associated with the character being viewed.
// builds/formats the list of highlights for each recruitment profile(this is
// not necessarily going to be the same as the character of the page being viewed)
// can make probably make some of this into shared utils that guild can use too
export const characterRecruitmentProfilesSelector = createSelector(
  characterRecruitmentPageStateSelector,
  profilesMainCharacterDetailsSelector,
  allRaidsSelector,
  ({ profiles }, allRaids) =>
    _.map(profiles, (p) => {
      // todo: memoize the highlight builders
      const profile = serializeRecruitmentProfile({}, p);
      const mainCharacterForProfile = _.find(profile.characters, { ordinal: 0 });

      return {
        profile,
        progressHighlights: [
          { label: 'ILVL', value: mainCharacterForProfile.character.itemLevelEquipped || '-' },
          ...getProgressHighlights(profile, p.details, allRaids)
        ],
        requirementHighlights: getRequirementHighlights(profile),
        scheduleHighlights: getScheduleHighlights(profile)
      };
    })
);

export const targetProfileSelector = createSelector(
  targetProfileIdSelector,
  characterRecruitmentProfilesSelector,
  (targetProfileId, profiles) =>
    targetProfileId > 0 ? _.find(profiles, (p) => p.profile.id === targetProfileId) : undefined
);
